import { pickBy } from 'lodash';
import moment from 'moment';
import { Measurement, MeasurementDimension } from '../../../query/query.types';
import { isQueryableMetric } from '../../../query/measurements';

export const MIN_LOOKBACK_DURATION = 30 * 24 * 3600; // 30 days in seconds

/**
 * Virtual NMS dimensions that are cross-compatible with all NMS measurements.
 * These aren't returned by the API schema but are accepted as filter fields by the NMS backend.
 * Mirrors the extra options appended in MetricsExplorerSidebar.availableDimensionFilterOptions.
 */
const NMS_VIRTUAL_DIMENSIONS: Record<string, MeasurementDimension> = {
  i_device_label: {
    Label: 'Device Label',
    Type: 'string',
    FilterOnly: true
  },
  i_output_interface_group: {
    Label: 'Interface Group',
    Type: 'string'
  }
};

/** Merge virtual NMS dimensions into a measurement's Dimensions, only for keys not already present. */
function injectVirtualDimensions(m: Measurement): Measurement {
  const existing = m.storage?.Dimensions ?? {};
  const toInject: Record<string, MeasurementDimension> = {};
  for (const [key, dim] of Object.entries(NMS_VIRTUAL_DIMENSIONS)) {
    if (!(key in existing)) {
      toInject[key] = dim;
    }
  }
  if (Object.keys(toInject).length === 0) {
    return m;
  }
  return {
    ...m,
    storage: {
      ...m.storage,
      Dimensions: { ...toInject, ...existing }
    }
  };
}

function prefixMeasurementPaths(rawMeasurements: Measurement[]): Measurement[] {
  return rawMeasurements.map((m) =>
    injectVirtualDimensions({
      ...m,
      measurement: m.measurement.startsWith('/metrics')
        ? m.measurement
        : `/metrics${m.measurement.startsWith('/') ? '' : '/'}${m.measurement}`
    })
  );
}

export function filterRecentMeasurements(measurements: Measurement[], lookback: number): Measurement[] {
  const sanitizedDuration = Math.max(lookback, MIN_LOOKBACK_DURATION);
  const deadline = moment().subtract(sanitizedDuration, 'seconds');

  return measurements
    .map((measurement) => {
      if (!measurement.last_seen) {
        return measurement;
      }
      if (deadline.isAfter(moment(measurement.last_seen))) {
        return null;
      }
      return {
        ...measurement,
        storage: {
          ...measurement.storage,
          Metrics: pickBy(
            measurement.storage.Metrics,
            (metric) => isQueryableMetric(metric) && deadline.isBefore(moment(metric.LastSeen))
          ),
          Dimensions: pickBy(measurement.storage.Dimensions, (dimension) =>
            deadline.isBefore(moment(dimension.LastSeen))
          )
        }
      };
    })
    .filter((m) => m !== null)
    .filter((m) => Object.keys(m.storage.Metrics).length > 0);
}

export const transformAndFilterReconMeasurements = (
  measurements: Measurement[],
  options?: { lookback?: number }
): Measurement[] => {
  // If no lookback is provided, return all measurements with prefixed paths
  if (options?.lookback === undefined) {
    const filtered = measurements.map((m) => ({
      ...m,
      storage: {
        ...m.storage,
        Metrics: pickBy(m.storage.Metrics, isQueryableMetric)
      }
    }));
    return prefixMeasurementPaths(filtered);
  }

  const recentMeasurements = filterRecentMeasurements(measurements, options.lookback);

  return prefixMeasurementPaths(recentMeasurements);
};
